import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";

import Header from "../../../components/common/Header";
import Footer from "../../../components/common/Footer";
import BackToTop from "../../../components/common/BackToTop";
import Loading from "../../../components/common/Loading";
import PaymentModal from "../../../components/common/PaymentModal";
import { useProjectDetail } from "../../../services/projectService";

const ProjectDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null
  );
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const { data: project, isLoading, error } = useProjectDetail(Number(id));

  // Trigger animations when data loads
  useEffect(() => {
    if (!isLoading && project) {
      setIsDataLoaded(true);
    }
  }, [isLoading, project]);

  if (isLoading) {
    return (
      <div className="min-h-screen w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
        <Header />
        <main className="py-16 px-4 sm:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <Loading />
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
        <Header />
        <main className="py-16 px-4 sm:px-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-4">
              {error ? "Error Loading Project" : "Project Not Found"}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
              {error
                ? "There was an error loading the project details. Please try again later."
                : "The project you're looking for doesn't exist."}
            </p>
            <button
              onClick={() => navigate("/projects")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Back to Projects
            </button>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </div>
    );
  }

  const progressPercentage =
    (project.currentFunding / project.targetFunding) * 100;
  const remaining = project.targetFunding - project.currentFunding;

  return (
    <div className="min-h-screen w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
      <Header />
      <main>
        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={isDataLoaded ? { opacity: 1 } : { opacity: 0 }}
          className="relative h-96 md:h-[500px] overflow-hidden"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${project.image})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/40" />
          <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={
                isDataLoaded ? { y: 0, opacity: 1 } : { y: 50, opacity: 0 }
              }
              transition={{ delay: 0.2 }}
              className="max-w-4xl"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={
                  isDataLoaded
                    ? { scale: 1, opacity: 1 }
                    : { scale: 0.9, opacity: 0 }
                }
                transition={{ delay: 0.4 }}
                className="inline-block mb-6"
              >
                <span className="bg-white/20 backdrop-blur-md px-4 py-2 rounded-full text-sm font-medium border border-white/30">
                  🤝 Community Project
                </span>
              </motion.div>
              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight drop-shadow-2xl">
                {project.title}
              </h1>
              <p className="text-xl md:text-2xl text-white/95 mb-10 font-light leading-relaxed max-w-3xl mx-auto">
                {project.description}
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsPaymentModalOpen(true)}
                className="bg-gradient-to-r from-emerald-600 to-stone-600 hover:from-emerald-700 hover:to-stone-700 text-white px-10 py-5 rounded-2xl font-bold text-lg shadow-2xl transition-all duration-300 border-2 border-white/30"
              >
                {t("projects.contribute")} 💚
              </motion.button>
            </motion.div>
          </div>
        </motion.section>

        {/* Project Details */}
        <motion.section
          initial={{ opacity: 0, y: 50 }}
          animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="py-16 px-4 sm:px-16"
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2 space-y-8">
                {/* Project Overview */}
                <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl">
                  <h2 className="text-3xl font-bold mb-6 text-emerald-600 dark:text-emerald-400">
                    Project Overview
                  </h2>
                  <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                    {project.description}
                  </p>
                </div>

                {/* Project Aim */}
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={
                    isDataLoaded ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }
                  }
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: 0.6 }}
                  className="bg-gradient-to-r from-emerald-50 to-stone-50 dark:from-emerald-900/20 dark:to-stone-900/20 p-8 rounded-2xl border-l-4 border-emerald-500"
                >
                  <h3 className="text-2xl font-bold mb-4 text-emerald-700 dark:text-emerald-300">
                    🎯 Project Aim
                  </h3>
                  <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                    {project.aim}
                  </p>
                </motion.div>

                {/* Current State */}
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={
                    isDataLoaded ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }
                  }
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="bg-gradient-to-r from-stone-50 to-emerald-50 dark:from-stone-900/20 dark:to-emerald-900/20 p-8 rounded-2xl border-l-4 border-stone-500"
                >
                  <h3 className="text-2xl font-bold mb-4 text-stone-700 dark:text-stone-300">
                    📈 Current State
                  </h3>
                  <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                    {project.currentState}
                  </p>
                </motion.div>

                {/* Project Timeline */}
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={
                    isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }
                  }
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl"
                >
                  <h3 className="text-2xl font-bold mb-6 text-emerald-600 dark:text-emerald-400">
                    ⏰ Project Timeline
                  </h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="bg-emerald-100 dark:bg-emerald-900 p-4 rounded-lg mb-2">
                        <span className="text-2xl">📅</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                        Start Date
                      </h4>
                      <p className="text-gray-600 dark:text-gray-400">
                        {new Date(project.startDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="bg-stone-100 dark:bg-stone-900 p-4 rounded-lg mb-2">
                        <span className="text-2xl">🎯</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                        Target Completion
                      </h4>
                      <p className="text-gray-600 dark:text-gray-400">
                        {new Date(project.endDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="bg-emerald-100 dark:bg-emerald-900 p-4 rounded-lg mb-2">
                        <span className="text-2xl">📍</span>
                      </div>
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                        Location
                      </h4>
                      <p className="text-gray-600 dark:text-gray-400">
                        {project.location}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              <div className="space-y-8">
                {/* Funding Progress */}
                <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-xl">
                  <h3 className="text-xl font-bold mb-4 text-emerald-600 dark:text-emerald-400">
                    💰 Funding Progress
                  </h3>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm font-medium mb-2">
                      <span>{t("projects.fundingProgress")}</span>
                      <span>
                        {progressPercentage.toFixed(0)}% {t("projects.funded")}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progressPercentage}%` }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="bg-gradient-to-r from-emerald-500 to-stone-500 h-4 rounded-full"
                      />
                    </div>
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mt-2">
                      <span>
                        ${project.currentFunding.toLocaleString()}{" "}
                        {t("projects.funded")}
                      </span>
                      <span>
                        ${remaining.toLocaleString()} {t("projects.remaining")}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200 px-3 py-1 rounded-full text-sm font-medium">
                      {project.category}
                    </span>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${project.status === "planned"
                        ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200"
                        : project.status === "active"
                          ? "bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200"
                          : project.status === "completed"
                            ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                            : "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
                        }`}
                    >
                      {t(`projects.status.${project.status}`)}
                    </span>
                  </div>
                </div>

                {/* Why Donate? */}
                <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-xl">
                  <h3 className="text-xl font-bold mb-4 text-stone-600 dark:text-stone-400">
                    🤝 Why Donate?
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <span className="text-emerald-500 text-xl">✓</span>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                          Make a Real Impact
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Your contribution directly supports sustainable
                          development in our community.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="text-stone-500 text-xl">✓</span>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                          Transparent Funding
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Track how your donation is used with our transparent
                          reporting system.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="text-emerald-500 text-xl">✓</span>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                          Community Benefit
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Help build a stronger, more prosperous Djichami for
                          everyone.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Project Coordinators */}
        {project.coordinators && project.coordinators.length > 0 && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={isDataLoaded ? { opacity: 1 } : { opacity: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8 }}
            className="py-16 px-4 sm:px-16 bg-gradient-to-br from-emerald-50/50 to-stone-50/50 dark:from-gray-800 dark:to-gray-900"
          >
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900 dark:text-gray-100">
                👥 Project Coordinators
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {project.coordinators.map((coordinator, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 50 }}
                    animate={
                      isDataLoaded
                        ? { opacity: 1, y: 0 }
                        : { opacity: 0, y: 50 }
                    }
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-700 p-6 rounded-2xl shadow-lg text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                  >
                    <img
                      src={coordinator.image}
                      alt={coordinator.name}
                      className="w-44 h-44 rounded-xl mx-auto mb-4 object-cover border-4 border-emerald-200 dark:border-emerald-700"
                    />
                    <h3 className="text-xl font-semibold mb-2">
                      {coordinator.name}
                    </h3>
                    <p className="text-emerald-600 dark:text-emerald-400 font-medium mb-3">
                      {coordinator.role}
                    </p>
                    {coordinator.bio && (
                      <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                        {coordinator.bio}
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>
        )}

        {/* Project Gallery */}
        {project.gallery && project.gallery.length > 0 && (
          <motion.section
            initial={{ opacity: 0 }}
            animate={isDataLoaded ? { opacity: 1 } : { opacity: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8 }}
            className="py-16 px-4 sm:px-16 bg-gradient-to-br from-stone-50/50 to-emerald-50/50 dark:from-gray-800 dark:to-gray-900"
          >
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900 dark:text-gray-100">
                📸 Project Gallery
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {project.gallery.map((image, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={
                      isDataLoaded
                        ? { opacity: 1, scale: 1 }
                        : { opacity: 0, scale: 0.8 }
                    }
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="relative group cursor-pointer overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300"
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img
                      src={image}
                      alt={`${project.title} - Image ${index + 1}`}
                      className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                      <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-lg font-semibold">
                        View
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>
        )}

        {/* Image Preview Modal */}
        <AnimatePresence>
          {selectedImageIndex !== null && project.gallery && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedImageIndex(null)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="relative max-w-4xl max-h-full"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={project.gallery[selectedImageIndex]}
                  alt={`${project.title} - Image ${selectedImageIndex + 1}`}
                  className="max-w-full max-h-full object-contain"
                />

                {/* Close Button */}
                <button
                  onClick={() => setSelectedImageIndex(null)}
                  className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>

                {/* Navigation Buttons */}
                {project.gallery.length > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setSelectedImageIndex(
                          selectedImageIndex === 0
                            ? project.gallery!.length - 1
                            : selectedImageIndex - 1
                        )
                      }
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 rounded-full p-3 transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() =>
                        setSelectedImageIndex(
                          selectedImageIndex === project.gallery!.length - 1
                            ? 0
                            : selectedImageIndex + 1
                        )
                      }
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 rounded-full p-3 transition-colors"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </>
                )}

                {/* Image Counter */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black/50 px-3 py-1 rounded-full text-sm">
                  {selectedImageIndex + 1} / {project.gallery.length}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Call to Action */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={isDataLoaded ? { opacity: 1 } : { opacity: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="py-20 px-4 sm:px-16 bg-gradient-to-r from-emerald-600 via-stone-600 to-emerald-700 text-white relative overflow-hidden"
        >
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={
                isDataLoaded ? { y: 0, opacity: 1 } : { y: 20, opacity: 0 }
              }
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <span className="text-6xl">💚</span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6">
              Ready to Make a Difference?
            </h2>
            <p className="text-xl md:text-2xl mb-10 opacity-95 font-light leading-relaxed max-w-2xl mx-auto">
              Join us in bringing this project to life. Every contribution
              counts!
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsPaymentModalOpen(true)}
                className="bg-white text-emerald-600 px-10 py-5 rounded-2xl font-bold text-lg shadow-2xl hover:bg-gray-50 transition-all duration-300 border-2 border-transparent hover:border-white/50"
              >
                {t("projects.contribute")} 🎯
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/projects")}
                className="border-2 border-white text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-white hover:text-emerald-600 transition-all duration-300 shadow-xl"
              >
                View All Projects
              </motion.button>
            </div>
          </div>
        </motion.section>
      </main>
      <Footer />
      <BackToTop />

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        projectTitle={project.title}
      />
    </div>
  );
};

export default ProjectDetail;
